import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types (SQLite shapes — booleans are 0/1) ───

export interface BookingSettingsRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  enabled: number | null; // 0/1
  public_path: string | null;
  default_appointment_status_id: string | null;
  // PRD.306 — automatic booking MENU reply to inbound WhatsApp messages.
  inbound_booking_menu_enabled: number | null; // 0/1
  /** 0 = every message, null = never repeat, N = wait N minutes. */
  inbound_booking_menu_repeat_minutes: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BookingProviderSettingRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  provider_id: string | null;
  enabled: number | null; // 0/1
  created_at: string | null;
  updated_at: string | null;
}

export interface BookingServiceSettingRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  service_id: string | null;
  enabled: number | null; // 0/1
  duration_override_mins: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BookingFieldSettingRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  field_key: string | null;
  enabled: number | null; // 0/1
  required: number | null; // 0/1
  locked: number | null; // 0/1
  section: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

export function qBookingSettings(): QuerySpec<[]> {
  return {
    key: ['booking-settings'] as const,
    sql: `SELECT * FROM booking_online_settings LIMIT 1`,
    params: [],
    watch: ['booking_online_settings'],
  };
}

export function qBookingProviderSettings(): QuerySpec<[]> {
  return {
    key: ['booking-provider-settings'] as const,
    sql: `SELECT * FROM booking_online_provider_settings ORDER BY provider_id ASC`,
    params: [],
    watch: ['booking_online_provider_settings'],
  };
}

export function qBookingServiceSettings(): QuerySpec<[]> {
  return {
    key: ['booking-service-settings'] as const,
    sql: `SELECT * FROM booking_online_service_settings ORDER BY service_id ASC`,
    params: [],
    watch: ['booking_online_service_settings'],
  };
}

export function qBookingFieldSettings(): QuerySpec<[]> {
  return {
    key: ['booking-field-settings'] as const,
    sql: `SELECT * FROM booking_online_patient_field_settings ORDER BY field_key ASC`,
    params: [],
    watch: ['booking_online_patient_field_settings'],
  };
}

// ─── Mappers ───

export function mapBookingSettingsToUI(row: BookingSettingsRow) {
  return {
    id: row.id,
    enabled: row.enabled === 1,
    publicPath: row.public_path ?? null,
    defaultAppointmentStatusId: row.default_appointment_status_id ?? null,
    // PRD.306. A legacy row predating these columns reads as undefined/null:
    // treat the toggle as ON (the documented default) and the interval as the
    // 24h default, so the UI shows the same thing the server will apply once
    // the row is saved.
    inboundBookingMenuEnabled:
      row.inbound_booking_menu_enabled == null ? true : row.inbound_booking_menu_enabled === 1,
    inboundBookingMenuRepeatMinutes:
      row.inbound_booking_menu_repeat_minutes === undefined
        ? DEFAULT_INBOUND_BOOKING_MENU_REPEAT_MINUTES
        : row.inbound_booking_menu_repeat_minutes,
  };
}

export function mapUIToBookingSettingsColumns(ui: {
  enabled: boolean;
  publicPath: string | null;
  defaultAppointmentStatusId: string | null;
  inboundBookingMenuEnabled: boolean;
  /** 0 = every message, null = never repeat. */
  inboundBookingMenuRepeatMinutes: number | null;
}) {
  return {
    enabled: ui.enabled ? 1 : 0,
    public_path: ui.publicPath,
    default_appointment_status_id: ui.defaultAppointmentStatusId,
    inbound_booking_menu_enabled: ui.inboundBookingMenuEnabled ? 1 : 0,
    // null is meaningful ("never repeat") and must survive as null.
    inbound_booking_menu_repeat_minutes: ui.inboundBookingMenuRepeatMinutes,
    updated_at: new Date().toISOString(),
  };
}

/** Stored repeat values + labels for the Online Booking settings UI (FR4). */
export const INBOUND_BOOKING_MENU_REPEAT_OPTIONS: ReadonlyArray<{
  value: number | null;
  label: string;
}> = [
  { value: 0, label: 'Every message' },
  { value: 60, label: 'After 1 hour' },
  { value: 360, label: 'After 6 hours' },
  { value: 720, label: 'After 12 hours' },
  { value: 1440, label: 'After 24 hours' },
  { value: 4320, label: 'After 3 days' },
  { value: null, label: 'Never repeat' },
];

/** Default repeat interval — 24 hours (PRD.306 FR4). */
export const DEFAULT_INBOUND_BOOKING_MENU_REPEAT_MINUTES = 1440;

export function mapBookingProviderToUI(row: BookingProviderSettingRow) {
  return {
    id: row.id,
    providerId: row.provider_id ?? '',
    enabled: row.enabled === 1,
  };
}

export function mapUIToBookingProviderColumns(ui: { enabled: boolean }) {
  return {
    enabled: ui.enabled ? 1 : 0,
    updated_at: new Date().toISOString(),
  };
}

export function mapBookingServiceToUI(row: BookingServiceSettingRow) {
  return {
    id: row.id,
    serviceId: row.service_id ?? '',
    enabled: row.enabled === 1,
    durationOverrideMins: row.duration_override_mins,
  };
}

export function mapUIToBookingServiceColumns(ui: {
  enabled: boolean;
  durationOverrideMins: number | null;
}) {
  return {
    enabled: ui.enabled ? 1 : 0,
    duration_override_mins: ui.durationOverrideMins,
    updated_at: new Date().toISOString(),
  };
}

export function mapBookingFieldToUI(row: BookingFieldSettingRow) {
  return {
    id: row.id,
    fieldKey: row.field_key ?? '',
    enabled: row.enabled === 1,
    required: row.required === 1,
    locked: row.locked === 1,
    section: row.section ?? '',
  };
}

export function mapUIToBookingFieldColumns(ui: {
  enabled: boolean;
  required: boolean;
  locked: boolean;
  section: string;
}) {
  return {
    enabled: ui.enabled ? 1 : 0,
    required: ui.required ? 1 : 0,
    locked: ui.locked ? 1 : 0,
    section: ui.section || null,
    updated_at: new Date().toISOString(),
  };
}
